import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import BenefitsChat from "./pages/BenefitsChat";
import CareerChat from "./pages/CareerChat";
import ProfileBuilder from "./pages/ProfileBuilder";
import DocumentGenerator from "./pages/DocumentGenerator";
import JobSearch from "./pages/JobSearch";
import LearningSuite from "./pages/LearningSuite";
import CourseViewer from "./pages/CourseViewer";
import ATSScoring from "./pages/ATSScoring";
import Membership from "./pages/Membership";
import MembershipSuccess from "./pages/MembershipSuccess";
import Testimonials from "./pages/Testimonials";
import Coaching from "./pages/Coaching";
import Community from "./pages/Community";
import About from "./pages/About";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-navy-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/testimonials" element={<Testimonials />} />
      <Route path="/about" element={<About />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/benefits-chat" element={
        <ProtectedRoute><BenefitsChat /></ProtectedRoute>
      } />
      <Route path="/career-chat" element={
        <ProtectedRoute><CareerChat /></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><ProfileBuilder /></ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute><DocumentGenerator /></ProtectedRoute>
      } />
      <Route path="/jobs" element={
        <ProtectedRoute><JobSearch /></ProtectedRoute>
      } />
      <Route path="/learning" element={
        <ProtectedRoute><LearningSuite /></ProtectedRoute>
      } />
      <Route path="/learning/:courseId" element={
        <ProtectedRoute><CourseViewer /></ProtectedRoute>
      } />
      <Route path="/ats-scoring" element={
        <ProtectedRoute><ATSScoring /></ProtectedRoute>
      } />
      <Route path="/coaching" element={
        <ProtectedRoute><Coaching /></ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute><Community /></ProtectedRoute>
      } />
      <Route path="/membership" element={
        <ProtectedRoute><Membership /></ProtectedRoute>
      } />
      <Route path="/membership/success" element={
        <ProtectedRoute><MembershipSuccess /></ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
